import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AddItemApplicationErrors,
  AddItemOptions,
  AddItemResponse,
  BulkUpdateWishlistTagsApplicationErrors,
  BulkUpdateWishlistTagsByFilterApplicationErrors,
  BulkUpdateWishlistTagsByFilterOptions,
  BulkUpdateWishlistTagsByFilterResponse,
  BulkUpdateWishlistTagsOptions,
  BulkUpdateWishlistTagsResponse,
  CatalogReference,
  GetDefaultWishlistResponse,
  RemoveItemOptions,
  RemoveItemResponse,
  addItem as universalAddItem,
  bulkUpdateWishlistTags as universalBulkUpdateWishlistTags,
  bulkUpdateWishlistTagsByFilter as universalBulkUpdateWishlistTagsByFilter,
  getDefaultWishlist as universalGetDefaultWishlist,
  removeItem as universalRemoveItem,
} from './ecom-v1-wishlist-wishlists.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ecom' };

/** @internal */
export function getDefaultWishlist(
  httpClient: HttpClient
): GetDefaultWishlistSignature {
  return () =>
    universalGetDefaultWishlist(
      // @ts-ignore
      { httpClient }
    );
}

interface GetDefaultWishlistSignature {
  /**
   * Returns the caller's default wishlist. Empty representation if none exists.
   */
  (): Promise<
    NonNullablePaths<
      GetDefaultWishlistResponse,
      | `wishlist.memberId`
      | `wishlist.items`
      | `wishlist.items.${number}.catalogReference.catalogItemId`
      | `wishlist.items.${number}.catalogReference.appId`
      | `wishlist.tags.privateTags.tagIds`,
      6
    >
  >;
}

/** @internal */
export function addItem(httpClient: HttpClient): AddItemSignature {
  return (
    catalogReference: NonNullablePaths<
      CatalogReference,
      `appId` | `catalogItemId`,
      2
    >,
    options?: AddItemOptions
  ) =>
    universalAddItem(
      catalogReference,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AddItemSignature {
  /**
   * Adds items to the caller's wishlist (auto-created on first use). Duplicates are skipped;
   * exceeding the 100-item cap returns WISHLIST_ITEMS_LIMIT_EXCEEDED.
   * @param - Item to add. Already present items (full three-field equality) are silently skipped.
   */
  (
    catalogReference: NonNullablePaths<
      CatalogReference,
      `appId` | `catalogItemId`,
      2
    >,
    options?: AddItemOptions
  ): Promise<
    NonNullablePaths<
      AddItemResponse,
      | `wishlist.memberId`
      | `wishlist.items`
      | `wishlist.items.${number}.catalogReference.catalogItemId`
      | `wishlist.items.${number}.catalogReference.appId`
      | `wishlist.tags.privateTags.tagIds`,
      6
    > & {
      __applicationErrorsType?: AddItemApplicationErrors;
    }
  >;
}

/** @internal */
export function removeItem(httpClient: HttpClient): RemoveItemSignature {
  return (
    catalogReference: NonNullablePaths<
      CatalogReference,
      `appId` | `catalogItemId`,
      2
    >,
    options?: RemoveItemOptions
  ) =>
    universalRemoveItem(
      catalogReference,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RemoveItemSignature {
  /**
   * Removes an item from the caller's wishlist. Absent item / no wishlist is a silent no-op.
   * @param - Item to remove. Absent items are silently skipped.
   */
  (
    catalogReference: NonNullablePaths<
      CatalogReference,
      `appId` | `catalogItemId`,
      2
    >,
    options?: RemoveItemOptions
  ): Promise<
    NonNullablePaths<
      RemoveItemResponse,
      | `wishlist.memberId`
      | `wishlist.items`
      | `wishlist.items.${number}.catalogReference.catalogItemId`
      | `wishlist.items.${number}.catalogReference.appId`
      | `wishlist.tags.privateTags.tagIds`,
      6
    >
  >;
}

/** @internal */
export function bulkUpdateWishlistTags(
  httpClient: HttpClient
): BulkUpdateWishlistTagsSignature {
  return (wishlistIds: string[], options?: BulkUpdateWishlistTagsOptions) =>
    universalBulkUpdateWishlistTags(
      wishlistIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateWishlistTagsSignature {
  /**
   * Synchronously assigns/unassigns tags on wishlists by ID.
   * @param - Wishlist IDs to update (1–100).
   */
  (wishlistIds: string[], options?: BulkUpdateWishlistTagsOptions): Promise<
    NonNullablePaths<
      BulkUpdateWishlistTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpdateWishlistTagsApplicationErrors;
    }
  >;
}

/** @internal */
export function bulkUpdateWishlistTagsByFilter(
  httpClient: HttpClient
): BulkUpdateWishlistTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: BulkUpdateWishlistTagsByFilterOptions
  ) =>
    universalBulkUpdateWishlistTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateWishlistTagsByFilterSignature {
  /**
   * Asynchronously assigns/unassigns tags on wishlists matching a filter
   * (empty filter = all wishlists belonging to the site_member_id). Returns a job_id.
   * @param - WQL filter selecting wishlists (empty = all belonging to the site_member_id).
   */
  (
    filter: Record<string, any>,
    options?: BulkUpdateWishlistTagsByFilterOptions
  ): Promise<
    NonNullablePaths<BulkUpdateWishlistTagsByFilterResponse, `jobId`, 2> & {
      __applicationErrorsType?: BulkUpdateWishlistTagsByFilterApplicationErrors;
    }
  >;
}

export {
  AccountInfo,
  ActionEvent,
  AddItemOptions,
  AddItemRequest,
  AddItemResponse,
  ApplicationError,
  BulkActionMetadata,
  BulkUpdateWishlistTagsByFilterOptions,
  BulkUpdateWishlistTagsByFilterRequest,
  BulkUpdateWishlistTagsByFilterResponse,
  BulkUpdateWishlistTagsOptions,
  BulkUpdateWishlistTagsRequest,
  BulkUpdateWishlistTagsResponse,
  BulkUpdateWishlistTagsResult,
  CatalogReference,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ExtendedFields,
  GetDefaultWishlistRequest,
  GetDefaultWishlistResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  MessageEnvelope,
  RemoveItemOptions,
  RemoveItemRequest,
  RemoveItemResponse,
  RestoreInfo,
  TagList,
  Tags,
  WebhookIdentityType,
  Wishlist,
  WishlistItem,
  WishlistItemAdded,
  WishlistItemRemoved,
  WishlistTagsModified,
} from './ecom-v1-wishlist-wishlists.universal.js';
